import { ChevronDown } from 'lucide-react';
import { useCallback, useEffect, useId, useRef, useState } from 'react';
import { Popover } from './Popover.js';
import type { SelectOption } from './Select.js';

export interface MultiSelectProps {
  /** Every value currently chosen. Empty means the filter is not narrowing. */
  selected: readonly string[];
  options: readonly SelectOption[];
  onToggle: (value: string) => void;
  onClear: () => void;
  /** What the trigger reads while nothing is chosen — "Every stage". */
  placeholder: string;
  /** The accessible name, where nothing visible beside the trigger names it. */
  label: string;
  /** The plural noun the trigger counts with — "stages". */
  noun: string;
}

/** How long a run of letters counts as one word being typed at the list. */
const TYPE_AHEAD_MS = 700;

/**
 * Select's shape with more than one answer: the same trigger, the same
 * popover, the same focus that never leaves the trigger. The list stays open
 * while choices are made, because the whole point of this control is that the
 * answer is several things.
 */
export function MultiSelect(props: MultiSelectProps): React.JSX.Element {
  const listId = useId();
  const [trigger, setTrigger] = useState<HTMLButtonElement | null>(null);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const optionNodes = useRef<(HTMLLIElement | null)[]>([]);
  const typed = useRef({ text: '', at: 0 });

  const { options, selected, onToggle } = props;

  const optionId = (index: number): string => `${listId}-option-${index}`;

  const step = useCallback(
    (from: number, delta: number): number => {
      const count = options.length;
      if (count === 0) return 0;
      let index = from;
      for (let tries = 0; tries < count; tries += 1) {
        index = (index + delta + count) % count;
        if (options[index]?.disabled !== true) return index;
      }
      return from;
    },
    [options],
  );

  const choose = useCallback(
    (index: number): void => {
      const option = options[index];
      if (option === undefined || option.disabled === true) return;
      onToggle(option.value);
    },
    [options, onToggle],
  );

  const typeAhead = useCallback(
    (key: string): void => {
      const now = Date.now();
      const text = (now - typed.current.at < TYPE_AHEAD_MS ? typed.current.text : '') + key;
      typed.current = { text, at: now };
      const needle = text.toLowerCase();
      const found = options.findIndex(
        (option) => option.disabled !== true && option.label.toLowerCase().startsWith(needle),
      );
      if (found !== -1) setActive(found);
    },
    [options],
  );

  function onKeyDown(event: React.KeyboardEvent<HTMLButtonElement>): void {
    if (!open) {
      if (['ArrowDown', 'ArrowUp', 'Enter', ' '].includes(event.key)) {
        event.preventDefault();
        setActive(step(-1, 1));
        setOpen(true);
      }
      return;
    }

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        setActive((index) => step(index, 1));
        return;
      case 'ArrowUp':
        event.preventDefault();
        setActive((index) => step(index, -1));
        return;
      case 'Home':
        event.preventDefault();
        setActive(step(-1, 1));
        return;
      case 'End':
        event.preventDefault();
        setActive(step(options.length, -1));
        return;
      case 'Enter':
      case ' ':
        event.preventDefault();
        choose(active);
        return;
      case 'Escape':
        event.preventDefault();
        setOpen(false);
        return;
      case 'Tab':
        setOpen(false);
        return;
      default:
        break;
    }

    if (event.key.length === 1 && !event.ctrlKey && !event.metaKey && !event.altKey) {
      event.preventDefault();
      typeAhead(event.key);
    }
  }

  useEffect(() => {
    if (!open) return;
    optionNodes.current[active]?.scrollIntoView({ block: 'nearest' });
  }, [open, active]);

  const only = selected.length === 1 ? options.find((o) => o.value === selected[0]) : undefined;
  const summary =
    selected.length === 0 ? props.placeholder : (only?.label ?? `${selected.length} ${props.noun}`);

  return (
    <>
      <button
        ref={setTrigger}
        type="button"
        role="combobox"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={open ? listId : undefined}
        aria-activedescendant={open ? optionId(active) : undefined}
        aria-label={props.label}
        className={selected.length === 0 ? 'select' : 'select narrowing'}
        onClick={() => {
          setActive(step(-1, 1));
          setOpen((current) => !current);
        }}
        onKeyDown={onKeyDown}
      >
        <span className={selected.length === 0 ? 'select-value placeholder' : 'select-value'}>
          {summary}
        </span>
        <ChevronDown className="select-chevron" aria-hidden="true" />
      </button>

      {open && trigger !== null && (
        <Popover
          anchor={{ kind: 'element', element: trigger }}
          onClose={(reason) => {
            setOpen(false);
            if (reason === 'escape') trigger.focus();
          }}
        >
          <ul
            id={listId}
            role="listbox"
            aria-multiselectable
            aria-label={props.label}
            className="listbox"
          >
            {options.map((option, index) => (
              <li
                key={option.value}
                id={optionId(index)}
                ref={(node) => {
                  optionNodes.current[index] = node;
                }}
                role="option"
                aria-selected={selected.includes(option.value)}
                aria-disabled={option.disabled}
                className={listboxOptionClass(index === active, selected.includes(option.value))}
                onMouseMove={() => {
                  if (option.disabled !== true) setActive(index);
                }}
                onClick={() => choose(index)}
              >
                <span className="listbox-mark" aria-hidden="true">
                  {selected.includes(option.value) ? '✓' : ''}
                </span>
                {option.label}
              </li>
            ))}
            {options.length === 0 && <li className="listbox-empty subtle">Nothing to choose.</li>}
            {selected.length > 0 && (
              <li className="listbox-footer">
                <button type="button" className="link-button" onClick={props.onClear}>
                  {props.placeholder}
                </button>
              </li>
            )}
          </ul>
        </Popover>
      )}
    </>
  );
}

function listboxOptionClass(isActive: boolean, isSelected: boolean): string {
  return `listbox-option${isActive ? ' active' : ''}${isSelected ? ' selected' : ''}`;
}
